import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

/** How much of the element must stay on screen, so it can always be grabbed
 *  back: a strip this wide sideways and this tall at the bottom edge. The top
 *  edge never leaves the window, since that is where the handle sits. */
const KEEP_VISIBLE_X = 96;
const KEEP_VISIBLE_Y = 48;

/** Pressing these starts their own action, never a drag. */
const INTERACTIVE =
  'button, a, input, select, textarea, label, summary, [contenteditable], [tabindex], [role="button"], [role="option"]';

/**
 * Whether the point sits on actual words, so pressing there selects text
 * (a product name, a document number) instead of picking the dialog up.
 * Words styled unselectable, like a dialog heading, still drag. Browsers
 * without caret hit-testing (and jsdom) always drag, as before.
 */
function overText(x: number, y: number): boolean {
  const node =
    document.caretPositionFromPoint?.(x, y)?.offsetNode ?? document.caretRangeFromPoint?.(x, y)?.startContainer;
  if (!node || node.nodeType !== Node.TEXT_NODE || !node.textContent?.trim()) return false;
  if (node.parentElement && getComputedStyle(node.parentElement).userSelect === 'none') return false;
  // The caret snaps to the nearest text even from blank space, so the point
  // must fall inside the text's own boxes.
  const range = document.createRange();
  range.selectNodeContents(node);
  return [...range.getClientRects()].some((r) => x >= r.left && x <= r.right && y >= r.top && y <= r.bottom);
}

interface DraggableOptions {
  /** What can be pressed to pick the element up. */
  handle: string;
  /** Parts of a handle that keep their own pointer behaviour, on top of the
   *  controls that always do (buttons, fields, links). */
  ignore?: string;
}

interface Offset {
  x: number;
  y: number;
}

/**
 * Lets the user move an element around the screen, e.g. to see the data
 * behind a dialog, by dragging any part of it that matches `handle`.
 *
 * The element keeps its normal layout and is moved with a transform, so
 * centring stays with CSS. Pointer events cover mouse, pen and touch alike.
 * During a drag the transform is written to the element directly rather than
 * through state, so a large form is not re-rendered on every pointer move.
 *
 * The body is zoomed by the interface scale, and pointer coordinates and
 * client rects are in screen pixels while a transform inside the zoomed body
 * is in CSS pixels, so the offset is divided by the zoom before it is applied.
 *
 * Double-clicking a handle puts the element back where it started.
 */
export function useDraggable(ref: RefObject<HTMLElement | null>, { handle, ignore }: DraggableOptions) {
  const offset = useRef<Offset>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  // Ends a drag in progress; kept so unmounting mid-drag (Escape closes the
  // dialog) does not leave window listeners behind.
  const stopDrag = useRef<(() => void) | null>(null);

  useEffect(() => () => stopDrag.current?.(), []);

  const apply = useCallback(
    (next: Offset) => {
      offset.current = next;
      const el = ref.current;
      if (!el) return;
      const zoom = el.currentCSSZoom || 1;
      el.style.translate = next.x === 0 && next.y === 0 ? '' : `${next.x / zoom}px ${next.y / zoom}px`;
    },
    [ref],
  );

  const isHandle = useCallback(
    (e: ReactMouseEvent<HTMLElement>) =>
      e.target instanceof Element &&
      e.target.closest(handle) !== null &&
      e.target.closest(ignore ? `${INTERACTIVE}, ${ignore}` : INTERACTIVE) === null &&
      !overText(e.clientX, e.clientY),
    [handle, ignore],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const el = ref.current;
      if (!el || !e.isPrimary || e.button !== 0 || !isHandle(e)) return;
      // No text selection or focus change while dragging.
      e.preventDefault();

      const start = { x: e.clientX, y: e.clientY };
      const from = offset.current;
      // Where the element would sit with no offset, to clamp against.
      const rect = el.getBoundingClientRect();
      const home = { left: rect.left - from.x, top: rect.top - from.y };
      const bounds = {
        minX: KEEP_VISIBLE_X - rect.width - home.left,
        maxX: window.innerWidth - KEEP_VISIBLE_X - home.left,
        minY: -home.top,
        maxY: window.innerHeight - KEEP_VISIBLE_Y - home.top,
      };
      const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

      function onMove(ev: PointerEvent) {
        if (ev.pointerId !== e.pointerId) return;
        apply({
          x: clamp(from.x + ev.clientX - start.x, bounds.minX, bounds.maxX),
          y: clamp(from.y + ev.clientY - start.y, bounds.minY, bounds.maxY),
        });
      }
      function onEnd(ev: PointerEvent) {
        if (ev.pointerId !== e.pointerId) return;
        stop();
        setDragging(false);
      }
      function stop() {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onEnd);
        window.removeEventListener('pointercancel', onEnd);
        stopDrag.current = null;
      }
      stopDrag.current?.();
      stopDrag.current = stop;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onEnd);
      window.addEventListener('pointercancel', onEnd);
      setDragging(true);
    },
    [ref, isHandle, apply],
  );

  const onDoubleClick = useCallback(
    (e: ReactMouseEvent<HTMLElement>) => {
      if (isHandle(e)) apply({ x: 0, y: 0 });
    },
    [isHandle, apply],
  );

  return { dragging, handleProps: { onPointerDown, onDoubleClick } };
}
